import { motion } from "framer-motion";
import type { RenderPayload } from "../../lib/agent-types";
import { centsToDollars } from "../../lib/financials";
import { formatLoanProduct } from "./render-registry.helpers";
import { AffordabilityScenariosWidget } from "./widgets/affordability-scenarios-widget";
import { CashToCloseWidget } from "./widgets/cash-to-close-widget";
import { DownPaymentComparisonWidget } from "./widgets/down-payment-comparison-widget";
import { DtiWidget } from "./widgets/dti-widget";
import { LoanProductPreviewWidget } from "./widgets/loan-product-preview-widget";
import { MonthlyPaymentWidget } from "./widgets/monthly-payment-widget";
import { RateExplanationWidget } from "./widgets/rate-explanation-widget";
import { RentVsBuyWidget } from "./widgets/rent-vs-buy-widget";

export function RenderPayloadView({ payload }: { payload: RenderPayload }) {
  const heading = getWidgetHeading(payload);
  const chipLabel = getWidgetChipLabel(payload);

  return (
    <motion.div
      className="space-y-2.5"
      data-slot="advisor-render-payload"
      layout
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
    >
      {chipLabel ? (
        <motion.div
          className="flex flex-wrap items-center gap-2"
          data-slot="advisor-widget-context-chip"
          layout
          transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-soft px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary-dark">
            <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-primary" />
            {chipLabel}
          </span>
          <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
            based on your profile
          </span>
        </motion.div>
      ) : null}
      {heading ? (
        <motion.div
          className="space-y-1"
          data-slot="advisor-widget-heading"
          layout
          transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-[15px] font-semibold leading-6 text-foreground">
            {heading}
          </p>
        </motion.div>
      ) : null}
      {renderWidget(payload)}
    </motion.div>
  );
}

// Short, type-level label for the context chip. Distinct from the
// rich `getWidgetHeading` ("Monthly payment on $550K home") because
// the chip's job is to anchor the *kind* of result; the heading does
// the parameter recap.
function getWidgetChipLabel(payload: RenderPayload): string | null {
  switch (payload.type) {
    case "monthly_payment":
      return "Monthly payment";
    case "dti":
      return "Debt-to-income";
    case "cash_to_close":
      return "Cash to close";
    case "affordability_scenarios":
      return "Affordability";
    case "rent_vs_buy":
      return "Rent vs. buy";
    case "down_payment_comparison":
      return "Down payment";
    case "loan_product_preview":
      return "Loan preview";
    case "rate_explanation":
      return "Rate explained";
    default:
      return null;
  }
}

function renderWidget(payload: RenderPayload) {
  switch (payload.type) {
    case "monthly_payment":
      return <MonthlyPaymentWidget payload={payload} />;
    case "dti":
      return <DtiWidget payload={payload} />;
    case "cash_to_close":
      return <CashToCloseWidget payload={payload} />;
    case "affordability_scenarios":
      return <AffordabilityScenariosWidget payload={payload} />;
    case "rent_vs_buy":
      return <RentVsBuyWidget payload={payload} />;
    case "down_payment_comparison":
      return <DownPaymentComparisonWidget payload={payload} />;
    case "loan_product_preview":
      return <LoanProductPreviewWidget payload={payload} />;
    case "rate_explanation":
      return <RateExplanationWidget payload={payload} />;
    default:
      return <RenderFallback payload={payload} />;
  }
}

function RenderFallback({ payload }: { payload: RenderPayload }) {
  const unknown = payload as { type: string; raw?: unknown };
  const text =
    typeof unknown.raw === "string"
      ? unknown.raw
      : JSON.stringify(unknown, null, 2);

  return (
    <div
      className="rounded-3xl bg-muted/40 px-4 py-3 text-sm text-foreground"
      data-slot="render-fallback"
      data-widget-type={unknown.type}
    >
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {unknown.type}
      </p>
      <p className="mt-2 whitespace-pre-wrap leading-6">{text}</p>
    </div>
  );
}

function getWidgetHeading(payload: RenderPayload) {
  switch (payload.type) {
    case "monthly_payment":
      return describeEstimateHeading(
        "Monthly payment",
        payload.estimate.request.property.target_price,
        payload.estimate.request.financing.down_payment_amount,
        payload.estimate.request.financing.down_payment_percent,
      );
    case "dti":
      return describeEstimateHeading(
        "DTI check",
        payload.estimate.request.property.target_price,
        payload.estimate.request.financing.down_payment_amount,
        payload.estimate.request.financing.down_payment_percent,
      );
    case "cash_to_close":
      return describeEstimateHeading(
        "Cash to close",
        payload.estimate.request.property.target_price,
        payload.estimate.request.financing.down_payment_amount,
        payload.estimate.request.financing.down_payment_percent,
      );
    case "affordability_scenarios": {
      const homePrice =
        payload.scenarios[0]?.estimate.request.property.target_price;
      return homePrice
        ? `Scenario comparison on ${formatCompactCurrencyFromCents(homePrice)} home`
        : "Scenario comparison";
    }
    case "rent_vs_buy": {
      const homePrice = payload.buy.request.property.target_price;
      return homePrice
        ? `Rent vs. buy on ${formatCompactCurrencyFromCents(homePrice)} home`
        : "Rent vs. buy";
    }
    case "down_payment_comparison": {
      const homePrice =
        payload.comparison?.homePrice ??
        payload.estimate.request.property.target_price;
      return homePrice
        ? `Down payment options on ${formatCompactCurrencyFromCents(homePrice)} home`
        : "Down payment options";
    }
    case "loan_product_preview": {
      const product = formatLoanProduct(
        payload.estimate.request.financing.product ??
          payload.estimate.results[0]?.raw.product,
      );
      const homePrice = payload.estimate.request.property.target_price;
      if (product && homePrice) {
        return `${product} preview on ${formatCompactCurrencyFromCents(homePrice)} home`;
      }
      if (product) {
        return `${product} preview`;
      }
      return homePrice
        ? `Loan product preview on ${formatCompactCurrencyFromCents(homePrice)} home`
        : "Loan product preview";
    }
    case "rate_explanation": {
      const homePrice = payload.estimate.request.property.target_price;
      return homePrice
        ? `Rate explained on ${formatCompactCurrencyFromCents(homePrice)} home`
        : "Rate explained";
    }
    default:
      return null;
  }
}

function describeEstimateHeading(
  label: string,
  homePrice: number | null | undefined,
  downPaymentAmount: number | null | undefined,
  downPaymentPercent: number | null | undefined,
) {
  const home = formatCompactCurrencyFromCents(homePrice);
  const down = describeDownPayment(downPaymentAmount, downPaymentPercent);

  if (home && down) {
    return `${label} on ${home} home with ${down}`;
  }
  if (home) {
    return `${label} on ${home} home`;
  }
  return label;
}

function describeDownPayment(
  downPaymentAmount: number | null | undefined,
  downPaymentPercent: number | null | undefined,
) {
  if (typeof downPaymentAmount === "number" && downPaymentAmount > 0) {
    return `${formatCompactCurrencyFromCents(downPaymentAmount)} down`;
  }
  const percent = normalizePercent(downPaymentPercent);
  return percent ? `${percent} down` : null;
}

function normalizePercent(value: number | null | undefined) {
  if (typeof value !== "number" || Number.isNaN(value) || value <= 0) {
    return null;
  }
  const percent = value <= 1 ? value * 100 : value;
  const rounded = Number(percent.toFixed(percent % 1 === 0 ? 0 : 1));
  return `${rounded}%`;
}

function formatCompactCurrencyFromCents(value: number | null | undefined) {
  if (typeof value !== "number" || Number.isNaN(value) || value <= 0) {
    return null;
  }

  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(centsToDollars(value));

  // Compact currency output varies across ICU builds (`$550K` vs `$550.0K`).
  return formatted.replace(/\.0(?=[KMBT])/g, "");
}

